import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { milestonesGridActions as actions } from '.';
import { homeActions } from 'app/pages/Home/slice';
import request from 'utils/request';
import { mapApiResponse } from 'utils/apiMapping';
import { selectDateRange } from 'app/pages/Home/slice/selectors';
import { selectMilestonesGrid } from './selectors';
import { ToastQueue } from '@adobe/react-spectrum';
import { milestonesColumnsConfig } from '../TableConfig/milestonesColumnsConfig';

function* loadMilestonesData(
  action: PayloadAction<{
    startDate?: string;
    endDate?: string;
  }>
) {
  try {
    // Get date range from home slice or use payload dates
    const dateRange = yield select(selectDateRange);
    const startDate = action.payload?.startDate || dateRange.start;
    const endDate = action.payload?.endDate || dateRange.end;

    // Get current state to check if initial load
    const currentState = yield select(selectMilestonesGrid);
    const isInitialLoad = currentState.initialLoad;

    // Show refreshing indicator only (do NOT dispatch loadReleaseDataSilent - that triggers moments API)
    if (!isInitialLoad) {
      yield put(homeActions.setSilentRefreshing(true));
    }

    console.log('📊 Fetching fresh milestones data from API');

    // Make API call to milestones-grid endpoint
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/moments/milestones-grid?startDate=${startDate}&endDate=${endDate}`
    );

    // Map inTheWorks (parking lot) to same shape as moments API tbd for ParkingLot component
    const payload = { ...response };
    if (payload.inTheWorks && Array.isArray(payload.inTheWorks)) {
      payload.inTheWorks = payload.inTheWorks.map((item: any) =>
        mapApiResponse(item)
      );
    }

    yield put(actions.milestonesDataLoaded(payload));

    if (!isInitialLoad) {
      yield put(homeActions.setSilentRefreshing(false));
    }
  } catch (e: any) {
    const currentState = yield select(selectMilestonesGrid);
    if (!currentState.initialLoad) {
      yield put(homeActions.setSilentRefreshing(false));
    }

    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred while loading milestones data'
    };

    yield put(actions.loadMilestonesDataError(errorInfo));

    // Show error toast
    ToastQueue.negative(errorInfo.message || 'Failed to load milestones data', {
      timeout: 5000
    });
  }
}

function* loadColumnConfig() {
  try {
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/moments/milestones-grid/column-config`
    );
    yield put(actions.columnConfigLoaded(response.columns));
  } catch (e: any) {
    // Fall back to static config so the grid always renders
    yield put(actions.columnConfigLoaded(milestonesColumnsConfig as any));
  }
}

export function* milestonesGridSaga() {
  yield takeLatest(actions.loadMilestonesData.type, loadMilestonesData);
  yield takeLatest(actions.loadColumnConfig.type, loadColumnConfig);
}
